import React from 'react';
import { Checkbox, Descriptions, Tag, Tooltip } from 'antd';
import { CalendarOutlined, CheckCircleOutlined, CloseCircleOutlined, UserOutlined } from '@ant-design/icons';
import { LedgerModel } from '@app/interfaces/ledger';
import { formatMoney } from '@app/utils/utils';
import { FaInfoCircle } from 'react-icons/fa';
import './Overview.css';
import UserAvatar from '@app/components/common/UserAvatar/UserAvatar';

interface LedgerInfoProps {
  record: LedgerModel;
}

const LABEL_STYLE = { fontWeight: 500, width: 180, background: '#fafafa', color: '#595959' };
const COLS = { xs: 1, sm: 2, md: 2 };

const getNamePayeeRecipient = (type: string) => {
  const map: Record<string, string> = {
    customer: 'Khách hàng',
    supplier: 'Nhà cung cấp',
    employee: 'Nhân viên',
    bank_account: 'Tài khoản ngân hàng',
    other: 'Khác',
  };
  return map[type] ?? '';
};

const Overview: React.FC<LedgerInfoProps> = ({ record }) => {
  const isReceivable = record.transaction_type === 'receivable';
  const nameLabel =
    record.payee_recipient_type === 'bank_account'
      ? isReceivable ? 'Tài khoản nộp' : 'Tài khoản nhận'
      : 'Họ và tên';

  return (
    <div className="ledger-overview">
      <Descriptions bordered size="small" column={COLS} labelStyle={LABEL_STYLE} className="ledger-overview__descriptions">
        <Descriptions.Item label="Mã phiếu">
          <span className="text-primary font-bold-weight">{record.ledger_code}</span>
        </Descriptions.Item>
        <Descriptions.Item label="Chi nhánh">
          <span className="text-primary font-bold-weight">{record.store?.name}</span>
        </Descriptions.Item>

        <Descriptions.Item label="Giá trị">
          <span className="text-primary font-bold-weight">{formatMoney(record.amount)}</span>
        </Descriptions.Item>
        <Descriptions.Item label="Loại thu/chi">
          <span className="text-primary">
            {record.transaction_type_name} —{' '}
            <strong>{record.payment_type_name}</strong>
          </span>
        </Descriptions.Item>

        <Descriptions.Item label="Nhân viên">
          <span className="text-primary">{record.employee_name}</span>
        </Descriptions.Item>
        <Descriptions.Item label={`Đối tượng ${isReceivable ? 'thu' : 'chi'}`}>
          <Tag color="blue">{getNamePayeeRecipient(record.payee_recipient_type)}</Tag>
        </Descriptions.Item>

        <Descriptions.Item label="Lý do nộp">
          <span className="text-primary">{record.name}</span>
        </Descriptions.Item>
        <Descriptions.Item label={nameLabel}>
          <span className="text-primary">{record.payee_recipient_description}</span>
        </Descriptions.Item>

        <Descriptions.Item label="Số tài khoản">
          <span className="text-primary">{record.account_number || '—'}</span>
        </Descriptions.Item>
        <Descriptions.Item label="Ghi chú">
          <span className="text-primary">{record.remark || '—'}</span>
        </Descriptions.Item>

        <Descriptions.Item label="Trạng thái">
          {record.posted ? (
            <Tag icon={<CheckCircleOutlined />} color="success">Đã thanh toán</Tag>
          ) : (
            <Tag icon={<CloseCircleOutlined />} color="error">Chưa thanh toán</Tag>
          )}
        </Descriptions.Item>
        <Descriptions.Item
          label={
            <span>
              Hoạch toán KQKD{' '}
              <Tooltip title="Chọn khi giá trị là khoản Thu nhập thêm hoặc Chi phí vận hành. Không chọn khi Thu tiền hàng hoặc Trả tiền NCC.">
                <FaInfoCircle style={{ cursor: 'help', color: '#8c8c8c' }} />
              </Tooltip>
            </span>
          }
        >
          <Checkbox checked={Boolean(record.recognize_income_statement)} />
        </Descriptions.Item>

        <Descriptions.Item label={<><UserOutlined /> Người tạo</>}>
          <UserAvatar name={record.created_by} avatarUrl={record.created_by_avatar_url} />
        </Descriptions.Item>
        <Descriptions.Item label={<><CalendarOutlined /> Ngày tạo</>}>
          <span className="text-primary">{record.created_date}</span>
        </Descriptions.Item>
      </Descriptions>

      {record.order_code && (
        <div className="text-danger ledger-overview__auto-note">
          Phiếu thu tự động được tạo gắn với hóa đơn{' '}
          <strong>{record.order_code}</strong>
        </div>
      )}
    </div>
  );
};

export default Overview;
